/*
	Dashboard page displaying user and module statistics
	Includes a summary of active modules and user count
	Uses custom components for user table and navigation
*/

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from '../features/users';
import { getModules, ModuleData } from '../features/modules';
import {
	faPuzzlePiece,
	faUsers,
	faClock,
	faHouse,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageHeader from 'layout/PageHeader';
import { isCurrentUserAdmin } from 'common/services/authUtils';

// TypeScript interface locale pour le Dashboard (données minimales nécessaires à l'affichage)
interface User {
	id?: number;
	firstName?: string;
	name?: string;
	email?: string;
	status?: string;
}

// Dashboard component
const Dashboard: React.FC = () => {
	// State management
	const [users, setUsers] = useState<User[]>([]);
	const [modules, setModules] = useState<ModuleData[]>([]);
	const [loading, setLoading] = useState(true);
	const [modulesLoading, setModulesLoading] = useState(true);
	const [currentTime, setCurrentTime] = useState(new Date());
	const navigate = useNavigate();

	// Déterminé une seule fois à l'initialisation du composant (ne change pas pendant la session)
	const isAdmin = isCurrentUserAdmin();

	// Fetch users and modules on component mount
	useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		// Fetch users uniquement si l'utilisateur est admin ou a accès au module utilisateurs
		if (isAdmin) {
			usersApi
				.listPaginated(token, { limit: 999999 })
				.then((res) => {
					const list = (res.data as unknown as { member: User[] }).member ?? [];
					setUsers(list);
				})
				.finally(() => setLoading(false));
		} else {
			setLoading(false);
		}

		// Fetch modules uniquement si admin (les paramètres de modules sont réservés aux admins)
		if (isAdmin) {
			getModules(token)
				.then((data) => setModules(data))
				.finally(() => setModulesLoading(false));
		} else {
			setModulesLoading(false);
		}

		// Update time every second
		const interval = setInterval(() => setCurrentTime(new Date()), 1000);
		return () => clearInterval(interval);
	}, [isAdmin]);

	// Count active modules
	const activeModulesCount = modules.filter((m) => m.enabled).length;

	// Render the dashboard
	return (
		<div className="min-h-screen bg-gray-100 p-6">
			<div className="max-w-6xl mx-auto">
				{/* Titre de page */}
				<PageHeader
					icon={faHouse}
					title="Tableau de bord"
					subtitle="Vos informations principales"
				/>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 mt-4">
					{/* Bloc "Modules activés" — réservé aux admins */}
					{isAdmin && (
						<div
							className="bg-white rounded-lg shadow p-4 flex items-center cursor-pointer hover:shadow-lg transition"
							onClick={() => navigate('/app/settings')}
						>
							<FontAwesomeIcon
								icon={faPuzzlePiece}
								size="2x"
								className="text-blue-500 mr-4"
							/>
							<div>
								<p className="text-sm text-gray-500">Modules activés</p>
								<p className="text-xl font-semibold">
									{modulesLoading ? '...' : activeModulesCount}
								</p>
							</div>
						</div>
					)}

					{/* Bloc "Nombre d'utilisateurs" — réservé aux admins (module users) */}
					{isAdmin && (
						<div
							className="bg-white rounded-lg shadow p-4 flex items-center cursor-pointer hover:shadow-lg transition"
							onClick={() => navigate('/app/users')}
						>
							<FontAwesomeIcon
								icon={faUsers}
								size="2x"
								className="text-blue-500 mr-4"
							/>
							<div>
								<p className="text-sm text-gray-500">
									Nombre d&apos;utilisateurs
								</p>
								<p className="text-xl font-semibold">
									{loading ? '...' : users.length}
								</p>
							</div>
						</div>
					)}

					{/* Bloc "Heure actuelle" — visible par tous */}
					<div className="bg-white rounded-lg shadow p-4 flex items-center">
						<FontAwesomeIcon
							icon={faClock}
							size="2x"
							className="text-blue-500 mr-4"
						/>
						<div>
							<p className="text-sm text-gray-500">Heure actuelle</p>
							<p className="text-xl font-semibold">
								{currentTime.toLocaleTimeString()}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

// Export the component
export default Dashboard;
